import React, { useState, useEffect } from 'react';
import Button from '../Button/Button';
import { ReactComponent as ViewMoreIcon } from '../../assets/icons/view-more.svg';
import { ReactComponent as PdfIcon } from '../../assets/icons/pdf.svg';
import { handlePrint } from '../../utils/commonHandlers';
import DetailsNotesModal from '../DetailsNotesModal/DetailsNotesModal'; // Ajoutez cette importation
import './SyntheseAnalyse.css';

interface Prestataire {
  id: string;
  presta_id: number;
  id_status: number;
  nom: string;
  dateReponse: string;
  offreTechnique: {
    note: number;
    url: string;
  };
  offreFinanciere: {
    montant: number;
    note: number;
  };
  note_financiere: number;
  noteTotal: number;
}

interface SyntheseAnalyseProps {
  prestataires: Prestataire[];
  selectedPrestataire: string | null;
  setSelectedPrestataire: (id: string) => void;
  onPrevious: () => void;
  onNext: () => void;
  threeColumnsLayout?: boolean;
  viewOnly?: boolean;
  generatePrestatairePDF?: (prestaId: number) => Promise<void>; // Nouvelle prop
  operation: any;
}

const SyntheseAnalyse: React.FC<SyntheseAnalyseProps> = ({
  prestataires,
  selectedPrestataire,
  setSelectedPrestataire,
  onPrevious,
  onNext,
  threeColumnsLayout = false,
  viewOnly = false,
  generatePrestatairePDF, // Récupérer la fonction
  operation
}) => {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [selectedPrestaDetails, setSelectedPrestaDetails] = useState<Prestataire | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  const getCandidatureDate = (commandeId: string) => {
    if (!operation?.historique) {
      console.warn('Operation historique is missing');
      return null;
    }

    const entry = operation.historique.find(
      (h: any) =>
        h.prefix === 'Candidature' &&
        h.destinataire?.toString() === commandeId?.toString()
    );

    if (!entry) {
      console.warn(`No candidature entry found for commande ${commandeId}`);
      return null;
    }

    return entry.created_at;
  };

  // Si aucun prestataire n'est disponible, afficher un message
  if (!prestataires || prestataires.length === 0) {
    return (
      <div className="empty-state">
        <p>Aucun prestataire n'est disponible pour l'analyse financière.</p>
      </div>
    );
  }

  // Exclure les prestataires expirés (id_status === 12) et refusés (id_status === 8)
  const prestatairesValides = prestataires.filter(p => p.id_status !== 12 && p.id_status !== 8);

  // Si aucun prestataire valide n'est disponible après filtrage, afficher un message
  if (prestatairesValides.length === 0) {
    return (
      <div className="empty-state">
        <p>Aucun prestataire n'a répondu à cette consultation ou toutes les candidatures ont expiré/été refusées.</p>
      </div>
    );
  }

  // Trouver le gagnant parmi les valides
  const gagnant = prestatairesValides.reduce((prev, current) =>
    (prev.noteTotal > current.noteTotal) ? prev : current
    , prestatairesValides[0]); // valeur initiale

  useEffect(() => {
    if (!selectedPrestataire && gagnant) {
      console.log("Sélection par défaut du gagnant:", gagnant.id);
      setSelectedPrestataire(gagnant.id);
    }
  }, [gagnant]); // ⚠️ il faut le mettre en dépendance

  const handleVoirDetails = (presta: Prestataire) => {
    setSelectedPrestaDetails(presta);
    setIsDetailsModalOpen(true);
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    setSuccessMsg('');
    try {
      const response = await fetch('/api/marche-subsequent', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'Brouillon', selectedPrestataire })
      });
      if (!response.ok) throw new Error('Erreur lors de la sauvegarde');
      setSuccessMsg('Votre brouillon a bien été sauvegardé.');
    } catch (e) {
      setSuccessMsg("Erreur lors de la sauvegarde du brouillon.");
    } finally {
      setIsSaving(false);
    }
  };


  // La note financière vient directement de l'API (presta.note_financiere)
  // Elle est fixe et ne doit pas être recalculée
  const getNoteFinanciere = (presta: Prestataire): number => {
    if (presta.id_status === 12) return 0; // expiré
    return presta.note_financiere || 0;
  };

  const calculateNoteGlobale = (presta: Prestataire): number => {
    if (presta.id_status === 12) return 0; // expiré
    const noteTech = presta.offreTechnique.note || 0;
    const noteFin = presta.offreFinanciere.note; // Utiliser la note financière de l'API
    const noteTotal = noteTech * 0.55 + noteFin * 0.45;
    return Math.round(noteTotal * 100) / 100;
  };

  // Gérer le clic sur une carte prestataire
  const handlePrestaClick = (prestaId: string) => {
    if (!viewOnly) {
      console.log("Prestataire sélectionné:", prestaId);
      setSelectedPrestataire(prestaId);
    }
  };

  // Ajoutez cette fonction en haut du composant pour déterminer le classement
  const getClassement = (presta: Prestataire, prestataires: Prestataire[]): number => {
    // Trier les prestataires par note totale (décroissant)
    const sorted = [...prestataires].sort((a, b) => b.noteTotal - a.noteTotal);
    // Trouver l'index (position) du prestataire actuel dans le tableau trié
    return sorted.findIndex(p => p.id === presta.id) + 1; // +1 car les index commencent à 0
  };

  // Fonction pour gérer le clic sur le bouton PDF d'un prestataire
  const handlePrestatairePDF = (prestaId: string) => {
    if (generatePrestatairePDF) {
      const numericId = parseInt(prestaId, 10);
      if (!isNaN(numericId)) {
        generatePrestatairePDF(numericId);
      }
    }
  };

  if (threeColumnsLayout) {
    return (
      <div className="step-container">
        {/* <div className="choix-prestataire-instructions">
          <p>Sélectionnez le prestataire de votre choix en cliquant sur sa carte. Le prestataire avec la meilleure note est présélectionné par défaut.</p>
        </div> */}

        <div className="presta-comparaison-grid">
          {prestataires.map((presta) => {
            const isGagnant = presta.id === gagnant.id && (presta.id_status !== 12 && presta.id_status !== 6);
            const isSelected = presta.id === selectedPrestataire;
            const isExpired = presta.id_status === 12;
            const isRefused = presta.id_status === 6;
            const candidatureDate = getCandidatureDate(presta.presta_id.toString());
            const isAccepted = presta.id_status === 11;
            const isPending = presta.id_status > 6 && presta.id_status < 11;


            return (
              <div
                className={`presta-col clickable ${isGagnant && !isExpired ? 'best-note' : (isExpired || isRefused) ? "disabled" : ""}`}
                key={presta.id}
                onClick={() => handlePrestaClick(presta.id)}
              >
                {/* Ajout du badge pour le prestataire avec la meilleure note */}
                {isGagnant && <div className="best-note-badge">Entreprise mieux disante</div>}

                {/* Ajout du badge pour les commandes expirées */}
                {isExpired && <div className="expired-badge">Expirée</div>}
                {isRefused && <div className="expired-badge">Refusée</div>}

                {isPending && <div className="best-note-badge">Signature en cours</div>}
                {isAccepted && <div className="best-note-badge">Acceptée</div>}

                <div className="presta-header">
                  <h3>{presta.nom}</h3>
                  {isExpired ? (
                    <span>Offre non reçue</span>
                  ) : (
                    <span>
                      Offre reçue le {
                        candidatureDate
                          ? `${new Date(candidatureDate).toLocaleDateString('fr-FR')} à ${new Date(candidatureDate).getHours().toString().padStart(2, '0')}h${new Date(candidatureDate).getMinutes().toString().padStart(2, '0')}`
                          : '-'
                      }
                    </span>
                  )
                  }
                </div>
                <div className="form-row note-ponderee">
                  <div className="form-group">
                    <label>Note technique :</label>
                    <div className="input-inactif">
                      {isExpired ? "-" : `${presta.offreTechnique.note.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/10`}
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Note pondérée :<span className="ponderation-badge tech">55%</span></label>
                    <div className="input-inactif pnd">
                      {isExpired ? (
                        "-"
                      ) : (
                        <>
                          {(presta.offreTechnique.note * 0.55).toLocaleString("fr-FR", {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}/5,50
                          <span className="note-detail">
                            {" "} (55% de{" "}
                            {presta.offreTechnique.note.toLocaleString("fr-FR", {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })})
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="form-row note-ponderee">
                  <div className="form-group">
                    <label>Note financière :</label>
                    <div className="input-inactif">
                      {presta.id_status === 12 ? "-" : `${presta.offreFinanciere.note.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/10`}                    </div>
                  </div>
                  <div className="form-group">
                    <label>Note pondérée :<span className="ponderation-badge fin">45%</span></label>
                    <div className="input-inactif pnd">
                      {isExpired ? "-" : (
                        <>
                          {(presta.offreFinanciere.note * 0.45).toLocaleString('fr-FR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2
                          })}/4,50
                          <span className="note-detail">
                            {" "} (45% de {presta.offreFinanciere.note.toLocaleString('fr-FR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2
                            })})
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>

                <div className="note-globale">
                  <div className="note-header">
                    <h4>Note globale :</h4>
                    {/* <div className="info-icon-container">
                      <svg className="info-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 0C3.6 0 0 3.6 0 8C0 12.4 3.6 16 8 16C12.4 16 16 12.4 16 8C16 3.6 12.4 0 8 0ZM8 12C7.4 12 7 11.6 7 11C7 10.4 7.4 10 8 10C8.6 10 9 10.4 9 11C9 11.6 8.6 12 8 12ZM9 9H7V5H9V9Z" fill="#4B5563"/>
                      </svg>
                      <div className="tooltip">
                        La note finale est calculée selon la formule: (Note technique × 0.55) + (Note financière × 0.45)
                      </div>
                    </div> */}
                  </div>
                  <div className="note-value">
                    {isExpired ? "-" : `${calculateNoteGlobale(presta).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / 10`}
                  </div>
                  {!isExpired && (
                    <div className="classement-indicator">
                      Entreprise classée n°{getClassement(presta, prestataires)}
                    </div>
                  )}
                </div>
                {/* {!isExpired && (

                  <div className="presta-footer-right">
                    {generatePrestatairePDF && (
                      <Button
                        value="PDF"
                        icon={<PdfIcon />}
                        variant="secondary"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation(); // Empêcher la propagation du clic pour ne pas sélectionner le prestataire
                          handlePrestatairePDF(presta.id);
                        }}
                      />
                    )}
                  </div>
                )} */}

                {!viewOnly && (
                  <div className="presta-selection-indicator">
                    {isSelected ? (
                      <div className="selected-label">Sélectionné</div>
                    ) : (
                      <div className="not-selected-label">Cliquez pour sélectionner</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {
          successMsg && (
            <div className="success-message">{successMsg}</div>
          )
        }

        {
          !viewOnly && (
            <div className="action-buttons">
              <Button
                value={isSaving ? "Enregistrement..." : "Enregistrer en brouillon"}
                variant="secondary"
                onClick={handleSaveDraft}
                disabled={isSaving}
              />
              <Button value="Précédent" variant="secondary" onClick={onPrevious} />
              <Button
                value="Suivant"
                variant="primary"
                onClick={onNext}
                disabled={!selectedPrestataire}
              />
            </div>
          )
        }
      </div >
    );
  }

  return (
    <div className="step-container">
      <h2>Analyse de l'offre financière</h2>
      <div className="tableau-notes">
        <table>
          <thead>
            <tr>
              <th>Date de réponse</th>
              <th>Offre Technique</th>
              <th>Note Technique</th>
              <th>Offre Financière</th>
              <th>Note Financière</th>
              <th>Note Total</th>
              <th>Sélectionner</th>
              {generatePrestatairePDF && <th>PDF</th>} {/* Nouvelle colonne pour PDF */}
            </tr>
          </thead>
          <tbody>
            {prestataires.map((presta) => (
              <tr key={presta.id} className={presta.id === gagnant.id ? "highlight" : ""}>
                <td>{presta.dateReponse}</td>
                <td>
                  <div
                    className="voir-details"
                    onClick={() => handleVoirDetails(presta)}
                    style={{ cursor: 'pointer' }}
                  >
                    Voir les détails
                    <ViewMoreIcon />
                  </div>
                </td>
                <td>{presta.offreTechnique.note}/10</td>
                <td>{presta.offreFinanciere.montant} €</td>
                <td>{presta.note_financiere}/10</td>
                <td className='note-total'>{presta.noteTotal}/10</td>
                <td>
                  <input
                    type="radio"
                    name="selectedPresta"
                    checked={selectedPrestataire === presta.id}
                    onChange={() => setSelectedPrestataire(presta.id)}
                  />
                </td>
                {generatePrestatairePDF && (
                  <td>
                    <Button
                      value=""
                      icon={<PdfIcon />}
                      variant="secondary"
                      size="small"
                      onClick={() => handlePrestatairePDF(presta.id)}
                    />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="selection-section">
        <p className="winner-info">
          Prestataire gagnant de ce marché avec la note de {gagnant.noteTotal}/10 est :
          <span className="winner">{gagnant.nom}</span>
        </p>
        <div className="choice-section">
          <label>Choisir un autre prestataire que celui gagnant :</label>
          <select
            value={selectedPrestataire || gagnant.id}
            onChange={(e) => setSelectedPrestataire(e.target.value)}
          >
            <option value={gagnant.id}>{gagnant.nom} (gagnant)</option>
            {prestataires
              .filter(p => p.id !== gagnant.id)
              .map(p => (
                <option key={p.id} value={p.id}>{p.nom}</option>
              ))
            }
          </select>
        </div>
      </div>

      <div className="action-buttons">
        <Button
          value={isSaving ? "Enregistrement..." : "Enregistrer en brouillon"}
          variant="secondary"
          onClick={handleSaveDraft}
          disabled={isSaving}
        />
        <Button
          variant="secondary"
          value="Précédent"
          onClick={onPrevious}
        />
        <Button
          variant="primary"
          value="Suivant"
          onClick={onNext}
          disabled={!selectedPrestataire}
        />
      </div>
      {isSaving && (
        <div style={{ textAlign: 'center', margin: '2rem 0' }}>
          <div className="loader" />
        </div>
      )}

      <DetailsNotesModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        critereOrganisation={{
          note: 8,
          commentaire: "Lorem ipsum dolor sit amet, consectetur adipiscing elit."
        }}
        critereMoyen={{
          note: selectedPrestaDetails?.note_financiere || 0,
          commentaire: "Lorem ipsum dolor sit amet, consectetur adipiscing elit."
        }}
        critereSocial={{
          note: 7,
          commentaire: "Lorem ipsum dolor sit amet, consectetur adipiscing elit."
        }}
      />
    </div>
  );
};

export default SyntheseAnalyse;